import type { EconomyNaming } from '@shared';
import { apiUrl } from './api';

const numberFormatter = new Intl.NumberFormat('en-US');

const relativeFormatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

export function formatCredits(value: number): string {
  return numberFormatter.format(value);
}

/**
 * What this creator calls her money, in the capitalisation she stored it in.
 *
 * For a label — a stat tile, a column heading — where a capital is correct.
 * Both forms are stored rather than an `s` being appended, so the plural is
 * looked up rather than built: see {@link EconomyNaming}.
 */
export function creditUnit(naming: EconomyNaming, amount: number): string {
  return Math.abs(amount) === 1 ? naming.creditName : naming.creditNamePlural;
}

/**
 * The same word inside a sentence, where the stored capital would read as a
 * shout: "spend 10 moonpetals". Defaults to the plural, which is what a bare
 * mention ("earn moonpetals") wants.
 */
export function creditWord(naming: EconomyNaming, amount = 2): string {
  return creditUnit(naming, amount).toLowerCase();
}

/** "1,250 moonpetals" — an amount and its unit, ready to drop into a sentence. */
export function formatCreditAmount(naming: EconomyNaming, value: number): string {
  return `${formatCredits(value)} ${creditWord(naming, value)}`;
}

/** "3 hours ago" style label, from an ISO-8601 timestamp. */
export function formatRelative(iso: string, now = Date.now()): string {
  const deltaSeconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const thresholds: [limit: number, unit: Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [3600, 'minute'],
    [86_400, 'hour'],
    [604_800, 'day'],
  ];

  let divisor = 1;
  for (const [limit, unit] of thresholds) {
    if (Math.abs(deltaSeconds) < limit) {
      return relativeFormatter.format(Math.round(deltaSeconds / divisor), unit);
    }
    divisor = limit;
  }

  return relativeFormatter.format(Math.round(deltaSeconds / 604_800), 'week');
}

/**
 * "2h 05m 30s" style countdown, from a duration in milliseconds.
 *
 * Lives here rather than beside the wheel because more than one timer
 * renders one — the wheel cooldowns, and anything gated later.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}h ${pad(minutes)}m ${pad(seconds)}s` : `${minutes}m ${pad(seconds)}s`;
}

/**
 * A shop price in the guild's currency.
 *
 * Falls back to the plain number if the owner typed something that isn't an
 * ISO currency code — a mistyped setting must not blank out the price list.
 */
export function formatPrice(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

/**
 * Discord CDN avatar, falling back to the default embed avatar.
 *
 * Served **through our own backend** rather than straight from the CDN. Inside
 * the iframe `cdn.discordapp.com` is only reachable if a URL mapping was set up
 * by hand in the Developer Portal — and when it hasn't been, every avatar
 * quietly becomes a pair of initials with nothing to explain it. The API is
 * reached through Discord's proxy for every other call, so routing images the
 * same way makes them load for the same reason, with nothing to configure.
 */
export function avatarUrl(userId: string, avatarHash: string | null, size = 128): string {
  if (!avatarHash) {
    const index = (BigInt(userId) >> 22n) % 6n;
    return apiUrl(`/cdn/embed/avatars/${index}.png`);
  }

  const extension = avatarHash.startsWith('a_') ? 'gif' : 'png';
  return apiUrl(`/cdn/avatars/${userId}/${avatarHash}.${extension}?size=${size}`);
}

const DISCORD_CDN = 'https://cdn.discordapp.com';

/**
 * Rewrites an absolute Discord CDN URL so it loads inside the activity.
 *
 * The server stores these as ordinary absolute URLs — that is the only shape
 * that also fits a picture pasted by hand in the console — and this points the
 * Discord ones at our own backend, for the reason given on {@link avatarUrl}.
 *
 * Anything not on Discord's CDN is returned untouched: it will load in a
 * browser, and inside the activity only if its host is mapped in the Developer
 * Portal. Every caller falls back to an initial when an image fails, so an
 * unmapped host costs a picture rather than a broken layout.
 */
export function imageUrl(url: string): string {
  if (!url.startsWith(DISCORD_CDN)) return url;
  return apiUrl(`/cdn${url.slice(DISCORD_CDN.length)}`);
}
